// Full list of IANA Time Zones organized by region
// Source: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones

export interface TimezoneOption {
  value: string
  label: string
  offset: string
  abbr?: string
}

export interface TimezoneGroup {
  label: string
  options: TimezoneOption[]
}

// Helper function to get current offset for a timezone
export function getCurrentOffset(timezone: string): string {
  try {
    const date = new Date()
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }))
    const tzDate = new Date(
      date.toLocaleString("en-US", { timeZone: timezone })
    )
    const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60)
    const sign = offset >= 0 ? "+" : ""
    return `UTC${sign}${offset}`
  } catch {
    return ""
  }
}

// Popular timezones for quick access
export const POPULAR_TIMEZONES: TimezoneOption[] = [
  { value: "UTC", label: "UTC", offset: "UTC+0" },
  {
    value: "America/New_York",
    label: "Eastern Time (US & Canada)",
    offset: "UTC-5",
    abbr: "EST"
  },
  {
    value: "America/Chicago",
    label: "Central Time (US & Canada)",
    offset: "UTC-6",
    abbr: "CST"
  },
  {
    value: "America/Denver",
    label: "Mountain Time (US & Canada)",
    offset: "UTC-7",
    abbr: "MST"
  },
  {
    value: "America/Los_Angeles",
    label: "Pacific Time (US & Canada)",
    offset: "UTC-8",
    abbr: "PST"
  },
  { value: "Europe/London", label: "London", offset: "UTC+0", abbr: "GMT" },
  { value: "Europe/Paris", label: "Paris", offset: "UTC+1", abbr: "CET" },
  { value: "Europe/Berlin", label: "Berlin", offset: "UTC+1", abbr: "CET" },
  { value: "Europe/Kyiv", label: "Kyiv", offset: "UTC+2", abbr: "EET" },
  { value: "Europe/Moscow", label: "Moscow", offset: "UTC+3", abbr: "MSK" },
  { value: "Asia/Dubai", label: "Dubai", offset: "UTC+4" },
  {
    value: "Asia/Kolkata",
    label: "India Standard Time",
    offset: "UTC+5:30",
    abbr: "IST"
  },
  {
    value: "Asia/Shanghai",
    label: "China Standard Time",
    offset: "UTC+8",
    abbr: "CST"
  },
  { value: "Asia/Tokyo", label: "Tokyo", offset: "UTC+9", abbr: "JST" },
  { value: "Australia/Sydney", label: "Sydney", offset: "UTC+10", abbr: "AEDT" }
]

// Complete list grouped by region
export const TIMEZONE_GROUPS: TimezoneGroup[] = [
  {
    label: "Popular",
    options: POPULAR_TIMEZONES
  },
  {
    label: "Africa",
    options: [
      { value: "Africa/Abidjan", label: "Abidjan", offset: "UTC+0" },
      { value: "Africa/Accra", label: "Accra", offset: "UTC+0" },
      { value: "Africa/Addis_Ababa", label: "Addis Ababa", offset: "UTC+3" },
      { value: "Africa/Algiers", label: "Algiers", offset: "UTC+1" },
      { value: "Africa/Cairo", label: "Cairo", offset: "UTC+2" },
      { value: "Africa/Casablanca", label: "Casablanca", offset: "UTC+1" },
      { value: "Africa/Johannesburg", label: "Johannesburg", offset: "UTC+2" },
      { value: "Africa/Lagos", label: "Lagos", offset: "UTC+1" },
      { value: "Africa/Nairobi", label: "Nairobi", offset: "UTC+3" }
    ]
  },
  {
    label: "Americas",
    options: [
      {
        value: "America/Anchorage",
        label: "Anchorage",
        offset: "UTC-9",
        abbr: "AKST"
      },
      {
        value: "America/Argentina/Buenos_Aires",
        label: "Buenos Aires",
        offset: "UTC-3"
      },
      { value: "America/Bogota", label: "Bogota", offset: "UTC-5" },
      { value: "America/Caracas", label: "Caracas", offset: "UTC-4" },
      {
        value: "America/Chicago",
        label: "Chicago",
        offset: "UTC-6",
        abbr: "CST"
      },
      {
        value: "America/Denver",
        label: "Denver",
        offset: "UTC-7",
        abbr: "MST"
      },
      {
        value: "America/Halifax",
        label: "Halifax",
        offset: "UTC-4",
        abbr: "AST"
      },
      {
        value: "America/Los_Angeles",
        label: "Los Angeles",
        offset: "UTC-8",
        abbr: "PST"
      },
      { value: "America/Mexico_City", label: "Mexico City", offset: "UTC-6" },
      {
        value: "America/New_York",
        label: "New York",
        offset: "UTC-5",
        abbr: "EST"
      },
      {
        value: "America/Phoenix",
        label: "Phoenix",
        offset: "UTC-7",
        abbr: "MST"
      },
      { value: "America/Santiago", label: "Santiago", offset: "UTC-3" },
      { value: "America/Sao_Paulo", label: "São Paulo", offset: "UTC-3" },
      {
        value: "America/Toronto",
        label: "Toronto",
        offset: "UTC-5",
        abbr: "EST"
      },
      {
        value: "America/Vancouver",
        label: "Vancouver",
        offset: "UTC-8",
        abbr: "PST"
      }
    ]
  },
  {
    label: "Asia",
    options: [
      { value: "Asia/Almaty", label: "Almaty", offset: "UTC+6" },
      { value: "Asia/Baghdad", label: "Baghdad", offset: "UTC+3" },
      { value: "Asia/Bangkok", label: "Bangkok", offset: "UTC+7" },
      { value: "Asia/Dhaka", label: "Dhaka", offset: "UTC+6" },
      { value: "Asia/Dubai", label: "Dubai", offset: "UTC+4" },
      {
        value: "Asia/Hong_Kong",
        label: "Hong Kong",
        offset: "UTC+8",
        abbr: "HKT"
      },
      { value: "Asia/Jakarta", label: "Jakarta", offset: "UTC+7", abbr: "WIB" },
      {
        value: "Asia/Jerusalem",
        label: "Jerusalem",
        offset: "UTC+2",
        abbr: "IST"
      },
      { value: "Asia/Karachi", label: "Karachi", offset: "UTC+5", abbr: "PKT" },
      {
        value: "Asia/Kolkata",
        label: "Kolkata",
        offset: "UTC+5:30",
        abbr: "IST"
      },
      { value: "Asia/Kuala_Lumpur", label: "Kuala Lumpur", offset: "UTC+8" },
      { value: "Asia/Manila", label: "Manila", offset: "UTC+8", abbr: "PHT" },
      { value: "Asia/Seoul", label: "Seoul", offset: "UTC+9", abbr: "KST" },
      {
        value: "Asia/Shanghai",
        label: "Shanghai",
        offset: "UTC+8",
        abbr: "CST"
      },
      {
        value: "Asia/Singapore",
        label: "Singapore",
        offset: "UTC+8",
        abbr: "SGT"
      },
      { value: "Asia/Taipei", label: "Taipei", offset: "UTC+8" },
      {
        value: "Asia/Tehran",
        label: "Tehran",
        offset: "UTC+3:30",
        abbr: "IRST"
      },
      { value: "Asia/Tokyo", label: "Tokyo", offset: "UTC+9", abbr: "JST" }
    ]
  },
  {
    label: "Australia & Pacific",
    options: [
      {
        value: "Australia/Adelaide",
        label: "Adelaide",
        offset: "UTC+9:30",
        abbr: "ACDT"
      },
      {
        value: "Australia/Brisbane",
        label: "Brisbane",
        offset: "UTC+10",
        abbr: "AEST"
      },
      {
        value: "Australia/Melbourne",
        label: "Melbourne",
        offset: "UTC+10",
        abbr: "AEDT"
      },
      {
        value: "Australia/Perth",
        label: "Perth",
        offset: "UTC+8",
        abbr: "AWST"
      },
      {
        value: "Australia/Sydney",
        label: "Sydney",
        offset: "UTC+10",
        abbr: "AEDT"
      },
      {
        value: "Pacific/Auckland",
        label: "Auckland",
        offset: "UTC+12",
        abbr: "NZDT"
      },
      { value: "Pacific/Fiji", label: "Fiji", offset: "UTC+12" },
      { value: "Pacific/Guam", label: "Guam", offset: "UTC+10" },
      {
        value: "Pacific/Honolulu",
        label: "Honolulu",
        offset: "UTC-10",
        abbr: "HST"
      }
    ]
  },
  {
    label: "Europe",
    options: [
      {
        value: "Europe/Amsterdam",
        label: "Amsterdam",
        offset: "UTC+1",
        abbr: "CET"
      },
      { value: "Europe/Athens", label: "Athens", offset: "UTC+2", abbr: "EET" },
      { value: "Europe/Berlin", label: "Berlin", offset: "UTC+1", abbr: "CET" },
      {
        value: "Europe/Brussels",
        label: "Brussels",
        offset: "UTC+1",
        abbr: "CET"
      },
      {
        value: "Europe/Bucharest",
        label: "Bucharest",
        offset: "UTC+2",
        abbr: "EET"
      },
      {
        value: "Europe/Budapest",
        label: "Budapest",
        offset: "UTC+1",
        abbr: "CET"
      },
      {
        value: "Europe/Copenhagen",
        label: "Copenhagen",
        offset: "UTC+1",
        abbr: "CET"
      },
      { value: "Europe/Dublin", label: "Dublin", offset: "UTC+0", abbr: "GMT" },
      {
        value: "Europe/Helsinki",
        label: "Helsinki",
        offset: "UTC+2",
        abbr: "EET"
      },
      { value: "Europe/Istanbul", label: "Istanbul", offset: "UTC+3" },
      { value: "Europe/Kyiv", label: "Kyiv", offset: "UTC+2", abbr: "EET" },
      { value: "Europe/Lisbon", label: "Lisbon", offset: "UTC+0", abbr: "WET" },
      { value: "Europe/London", label: "London", offset: "UTC+0", abbr: "GMT" },
      { value: "Europe/Madrid", label: "Madrid", offset: "UTC+1", abbr: "CET" },
      { value: "Europe/Moscow", label: "Moscow", offset: "UTC+3", abbr: "MSK" },
      { value: "Europe/Oslo", label: "Oslo", offset: "UTC+1", abbr: "CET" },
      { value: "Europe/Paris", label: "Paris", offset: "UTC+1", abbr: "CET" },
      { value: "Europe/Prague", label: "Prague", offset: "UTC+1", abbr: "CET" },
      { value: "Europe/Rome", label: "Rome", offset: "UTC+1", abbr: "CET" },
      {
        value: "Europe/Stockholm",
        label: "Stockholm",
        offset: "UTC+1",
        abbr: "CET"
      },
      { value: "Europe/Vienna", label: "Vienna", offset: "UTC+1", abbr: "CET" },
      { value: "Europe/Warsaw", label: "Warsaw", offset: "UTC+1", abbr: "CET" },
      { value: "Europe/Zurich", label: "Zurich", offset: "UTC+1", abbr: "CET" }
    ]
  }
]

// Flat list of all timezones
export const ALL_TIMEZONES: TimezoneOption[] = TIMEZONE_GROUPS.flatMap(
  (group) => group.options
)

// Function to format timezone for display
export function formatTimezone(timezone: TimezoneOption): string {
  const offset = timezone.offset || ""
  const abbr = timezone.abbr ? ` (${timezone.abbr})` : ""
  return `${timezone.label} ${offset}${abbr}`
}

// Function to get user's current timezone
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Function to search timezones
export function searchTimezones(query: string): TimezoneOption[] {
  const lowerQuery = query.toLowerCase()
  return ALL_TIMEZONES.filter(
    (tz) =>
      tz.label.toLowerCase().includes(lowerQuery) ||
      tz.value.toLowerCase().includes(lowerQuery) ||
      (tz.abbr && tz.abbr.toLowerCase().includes(lowerQuery))
  )
}
